import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faCog,
    faImage,
    faToggleOn,
    faFilter,
    faTable,
    faSave,
    faPalette,
    faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import config from '../configs/config';
import { useEscapeKey } from './hooks/useEscapeKey';
import './styles/RowColumnProperties.css';

// Same normalization as RowColumnComponent.js, so a qType comparison here
// can't disagree with the one that decides which columns are offered there.
const normalizeType = (type) => String(type || "").replace(/\s+/g, "").toLowerCase();
const typeMatches = (a, b) => normalizeType(a) === normalizeType(b);

// Accepts 3- or 6-digit hex, with the leading '#' - the same shape both the
// native <input type="color"> and the manual hex field are expected to hold.
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// <input type="color"> only accepts a strict 7-char "#rrggbb" value - a
// blank, in-progress, or shorthand ("#f00") entry from the manual hex field
// would otherwise blank/reset the native picker. Expand/fall back so the
// picker swatch always renders something instead of erroring out.
const normalizeHexForPicker = (hex) => {
    if (!HEX_COLOR_REGEX.test(hex || '')) return '#ffffff';
    if (hex.length === 4) {
        return '#' + hex.slice(1).split('').map((c) => c + c).join('');
    }
    return hex;
};

// Turn "3-5, 9" into 0-based indexes [2,3,4,8]. Free text, so there's no
// invalid intermediate state to fight with like the old number fields had —
// whatever doesn't parse yet is just ignored until the user finishes typing.
const parseRowNumbers = (text) => {
    const set = new Set();
    (text || '').split(',').forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;
        const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
        if (rangeMatch) {
            let a = parseInt(rangeMatch[1], 10);
            let b = parseInt(rangeMatch[2], 10);
            if (a > b) [a, b] = [b, a];
            for (let n = a; n <= b; n++) if (n >= 1) set.add(n - 1);
            return;
        }
        const n = parseInt(trimmed, 10);
        if (!isNaN(n) && n >= 1) set.add(n - 1);
    });
    return [...set].sort((a, b) => a - b);
};

// Inverse of parseRowNumbers, for seeding the field when a header is opened.
const formatRowNumbers = (indexes) => {
    if (!indexes || indexes.length === 0) return '';
    const sorted = [...indexes].sort((a, b) => a - b);
    const parts = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i <= sorted.length; i++) {
        const cur = sorted[i];
        if (cur === prev + 1) {
            prev = cur;
            continue;
        }
        parts.push(start === prev ? `${start + 1}` : `${start + 1}-${prev + 1}`);
        start = cur;
        prev = cur;
    }
    return parts.join(', ');
};

// Click-to-show info icon explaining what a field/option means - same
// interaction as OESettingsComponent's CheckboxWithTooltip (click to open,
// click outside to close), just restyled for this popup. Safe to nest
// inside a <label> (whether it wraps a checkbox or points at another field
// via htmlFor): preventDefault+stopPropagation on the icon's own click stops
// the browser from forwarding that click to the label's associated control
// (toggling a checkbox, opening a file picker, etc).
const InfoIcon = ({ tooltip }) => {
    const [show, setShow] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!show) return;
        const handleOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setShow(false);
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [show]);

    if (!tooltip) return null;

    return (
        <span
            ref={ref}
            className="rcp-info-icon"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShow((s) => !s); }}
        >
            <FontAwesomeIcon icon={faInfoCircle} />
            {show && (
                <div className="rcp-info-tooltip" onClick={(e) => e.stopPropagation()}>
                    {tooltip}
                </div>
            )}
        </span>
    );
};

// Checkbox pill: same native checkbox + label wiring as before (identical
// onChange contract), just presented as a toggle chip like the row badges.
// `tooltip` is optional - renders a click-to-show info icon when passed.
const CheckPill = ({ id, label, checked, onChange, tooltip }) => (
    <label className={`rcp-check${checked ? ' rcp-check-on' : ''}`} htmlFor={id}>
        <input
            type="checkbox"
            id={id}
            checked={checked || false}
            onChange={onChange}
        />
        {label}
        <InfoIcon tooltip={tooltip} />
    </label>
);

// Label + info icon, for the plain text/number/select fields (not checkbox
// pills) - same htmlFor contract as a bare <label>, so existing focus
// behavior on click is unchanged.
const FieldLabel = ({ htmlFor, text, tooltip }) => (
    <label htmlFor={htmlFor}>
        {text}
        <InfoIcon tooltip={tooltip} />
    </label>
);

const RowColumnProperties = ({ isOpen, onClose, item, index, handlePropertyChange, qType }) => {
    // Row Numbers is edited as free text and parsed/committed on every
    // change, but the box itself always shows exactly what was typed rather
    // than a value derived back from the model — so a still-incomplete
    // range like "3-" never gets fought or overwritten mid-edit.
    const [rowNumbersText, setRowNumbersText] = useState('');
    const [imgUploading, setImgUploading] = useState(false);
    // Full-size image lightbox (click the small preview to open).
    const [imgZoomOpen, setImgZoomOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        // Seed from the item's current indexes regardless of whether it's a
        // header yet — the Header checkbox can be turned on for any row, so
        // this needs to be ready the moment the field appears.
        setRowNumbersText(formatRowNumbers(item?.indexes));
        setImgZoomOpen(false); // never carry an open lightbox to another item
        // Re-sync whenever a different item is opened, not on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, item?.id, index]);

    // Escape closes the topmost layer only - the zoom lightbox first (if
    // open), otherwise the popup itself - so it never skips a layer.
    useEscapeKey(isOpen, imgZoomOpen ? () => setImgZoomOpen(false) : onClose);

    if (!isOpen) return null;

    const handleImageFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            alert('Image must be under 3MB');
            return;
        }

        setImgUploading(true);
        try {
            // Upload to the backend (SURVEY_IMAGES folder, server-generated
            // filename) and store the returned URL - NOT a base64 data URL,
            // which used to bloat sqgroup_json and couldn't be reused as a
            // plain link by the PSNST survey pages.
            const formData = new FormData();
            formData.append('image', file);
            const response = await fetch(`${config.API_BASE_URL}/upload-survey-image`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            const data = await response.json();
            if (!response.ok || !data.success || !data.url) {
                throw new Error(data.error || 'Upload failed');
            }
            handlePropertyChange(index, 'imageURL', data.url);
        } catch (err) {
            console.error('Image upload failed:', err);
            alert(`Failed to upload image: ${err.message}`);
        } finally {
            setImgUploading(false);
            e.target.value = ''; // allow re-selecting the same file later
        }
    };

    return (
        <div className="rcp-overlay" onClick={() => onClose()}>
            <div className="rcp-content" onClick={(e) => e.stopPropagation()}>
                <div className="rcp-header">
                    <h3>
                        <FontAwesomeIcon icon={faCog} style={{ color: '#6366f1' }} />
                        {item?.header ? 'Header Properties' : 'Row/Column Properties'}
                    </h3>
                    <button className="rcp-close-btn" title="Close" onClick={() => onClose()}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="rcp-body">
                    {/* ── Basics ── */}
                    <div className="rcp-section">
                        <h4 className="rcp-section-title">Basics</h4>
                        <div className="rcp-grid">
                            <div className="rcp-field">
                                <FieldLabel htmlFor="index" text="Index" tooltip="The row/column's code, e.g. used in exports and logic (r1, c2...). Doesn't have to match its display order." />
                                <input
                                    type="text"
                                    id="index"
                                    value={item?.index || ''}
                                    onChange={(e) => handlePropertyChange(index, 'index', e.target.value)}
                                />
                            </div>
                            <div className="rcp-field">
                                <FieldLabel htmlFor="text" text="Label" tooltip="The text respondents actually see for this row/column." />
                                <input
                                    type="text"
                                    id="text"
                                    value={item?.text || ''}
                                    onChange={(e) => handlePropertyChange(index, 'text', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Image ── */}
                    <div className="rcp-section">
                        <h4 className="rcp-section-title">
                            <FontAwesomeIcon icon={faImage} /> Image
                        </h4>
                        <div className="rcp-grid">
                            <div className="rcp-field">
                                <FieldLabel htmlFor="imageURL" text="Image URL" tooltip="A direct link to an image shown alongside this row/column's label." />
                                <input
                                    type="text"
                                    id="imageURL"
                                    placeholder="https://example.com/image.png"
                                    value={item?.imageURL || ''}
                                    onChange={(e) => handlePropertyChange(index, 'imageURL', e.target.value)}
                                />
                            </div>
                            <div className="rcp-field">
                                <FieldLabel htmlFor="imageUpload" text="Or Upload Image" tooltip="Upload an image file instead of pasting a URL - it's hosted for you and the URL above fills in automatically." />
                                <input
                                    type="file"
                                    id="imageUpload"
                                    accept="image/*"
                                    onChange={handleImageFileChange}
                                    disabled={imgUploading}
                                />
                                {imgUploading && <span className="rcp-uploading">Uploading...</span>}
                            </div>
                        </div>
                        {item?.imageURL && (
                            <div className="rcp-img-preview">
                                <img
                                    src={item.imageURL}
                                    alt="preview"
                                    title="Click to zoom"
                                    onClick={() => setImgZoomOpen(true)}
                                />
                                <span className="rcp-img-zoom-hint">Click image to zoom</span>
                                <button
                                    type="button"
                                    className="rcp-img-remove-btn"
                                    title="Remove image"
                                    onClick={() => handlePropertyChange(index, 'imageURL', '')}
                                >
                                    Remove Image
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Color ── */}
                    {!item?.header && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faPalette} /> Color
                            </h4>
                            <div className="rcp-grid">
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="colorPicker" text="Pick Color" tooltip="A custom color to render this specific answer with, on top of the question's normal styling." />
                                    <input
                                        type="color"
                                        id="colorPicker"
                                        className="rcp-color-swatch-input"
                                        value={normalizeHexForPicker(item?.color)}
                                        onChange={(e) => handlePropertyChange(index, 'color', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="colorHex" text="Hex Code" tooltip="Same color, typed as a hex code (#RRGGBB or #RGB) - handy for pasting an exact brand color." />
                                    <input
                                        type="text"
                                        id="colorHex"
                                        placeholder="#RRGGBB"
                                        value={item?.color || ''}
                                        onChange={(e) => handlePropertyChange(index, 'color', e.target.value)}
                                        maxLength={7}
                                    />
                                </div>
                            </div>
                            {item?.color && (
                                <div className="rcp-color-preview-row">
                                    <span
                                        className="rcp-color-preview-swatch"
                                        style={{ background: HEX_COLOR_REGEX.test(item.color) ? item.color : 'transparent' }}
                                    />
                                    <span className="rcp-color-preview-text">
                                        {HEX_COLOR_REGEX.test(item.color) ? item.color : 'Not a valid hex color yet (e.g. #FF0000 or #F00)'}
                                    </span>
                                    <button
                                        type="button"
                                        className="rcp-img-remove-btn"
                                        onClick={() => handlePropertyChange(index, 'color', '')}
                                    >
                                        Remove Color
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Behavior ── */}
                    <div className="rcp-section">
                        <h4 className="rcp-section-title">
                            <FontAwesomeIcon icon={faToggleOn} /> Behavior
                        </h4>
                        <div className="rcp-checks">
                            <CheckPill
                                id="header"
                                label="Header"
                                checked={item?.header}
                                onChange={(e) => handlePropertyChange(index, 'header', e.target.checked)}
                                tooltip="Turns this into a section-heading row that groups other rows underneath it, instead of a normal answer option - it stops being exported as its own answer."
                            />

                            {!item?.header && (<>
                                {/* Single Response answers are inherently exclusive
                                    (only one can be picked), so the checkbox is
                                    redundant and hidden for that question type. */}
                                {!typeMatches(qType, "SingleResponse") && (
                                    <CheckPill
                                        id="exclusive"
                                        label="Exclusive"
                                        checked={item?.exclusive}
                                        onChange={(e) => handlePropertyChange(index, 'exclusive', e.target.checked)}
                                        tooltip="e.g. 'None of these' - selecting this option automatically deselects every other option in the same question."
                                    />
                                )}
                                <CheckPill
                                    id="screenout"
                                    label="Screen Out"
                                    checked={item?.screenout}
                                    onChange={(e) => handlePropertyChange(index, 'screenout', e.target.checked)}
                                    tooltip="Respondents who pick this option are terminated from the survey (screened out)."
                                />
                                <CheckPill
                                    id="other"
                                    label="Other"
                                    checked={item?.other}
                                    onChange={(e) => handlePropertyChange(index, 'other', e.target.checked)}
                                    tooltip="Shows an 'Other, please specify' text box when this option is selected. Configure that box below once enabled."
                                />
                            </>)}
                            <CheckPill
                                id="fix"
                                label="Fix"
                                checked={item?.fix}
                                onChange={(e) => handlePropertyChange(index, 'fix', e.target.checked)}
                                tooltip="Keeps this row/column pinned in place - it's excluded from randomization/rotation."
                            />

                        </div>

                        {item?.header && (
                            <>
                                <div className="rcp-field" style={{ marginTop: '12px' }}>
                                    <FieldLabel htmlFor="header-rownumbers" text="Row Numbers" tooltip="Which rows (by their current display position, 1-based) this header sits above and groups together." />
                                    <input
                                        type="text"
                                        id="header-rownumbers"
                                        placeholder="e.g. 3-5, 9"
                                        value={rowNumbersText}
                                        onChange={(e) => {
                                            setRowNumbersText(e.target.value);
                                            handlePropertyChange(index, 'indexes', parseRowNumbers(e.target.value));
                                        }}
                                    />
                                </div>
                                <p className="rcp-hint">
                                    Row numbers refer to the current row order (1 = first row). List every row that
                                    belongs to this header, comma-separated — single numbers ("3, 9") or ranges
                                    ("3-5, 9") both work, and rows don't need to be contiguous. To nest a header inside
                                    another, give it a set of row numbers that's entirely covered by the outer header's
                                    set. Note: a row marked as Header is used as a label only — it is no longer exported
                                    as its own answer option, so its Index/Label text becomes the header text instead.
                                </p>
                            </>
                        )}

                        {/* Other (Specify) Settings: lives purely at the row level
                            (not in OESettingsComponent.js's question-wide OE
                            Parameters) - a question typically only has one or two
                            rows marked Other, so a separate global default would
                            just be redundant with these. */}
                        {item?.other && (
                            <div className="rcp-other-settings" style={{ marginTop: 12 }}>
                                <p className="rcp-hint" style={{ marginBottom: 10 }}>
                                    Settings for the "Other, please specify" text box shown when this row is selected.
                                </p>
                                <CheckPill
                                    id="otherRequired"
                                    label="Other (Specify) Required"
                                    checked={item?.otherRequired !== false}
                                    onChange={(e) => handlePropertyChange(index, 'otherRequired', e.target.checked)}
                                    tooltip="If unchecked, respondents can select Other and move on without typing anything in the specify box."
                                />
                                <div className="rcp-grid" style={{ marginTop: 10 }}>
                                    <div className="rcp-field">
                                        <FieldLabel htmlFor="otherPiping" text="Other Piping" tooltip="Pipe this row's own typed-in Other text into later questions/logic, referencing it by this piping code." />
                                        <input
                                            type="text"
                                            id="otherPiping"
                                            value={item?.otherPiping || ''}
                                            onChange={(e) => handlePropertyChange(index, 'otherPiping', e.target.value)}
                                        />
                                    </div>
                                    <div className="rcp-field">
                                        <FieldLabel htmlFor="otherInputType" text="Input Type" tooltip="What kind of value the specify box accepts/validates - plain text, an email address, a phone number, a date, etc." />
                                        <select
                                            id="otherInputType"
                                            value={item?.otherInputType || 'text'}
                                            onChange={(e) => handlePropertyChange(index, 'otherInputType', e.target.value)}
                                        >
                                            <option value="text">Text</option>
                                            <option value="email">Email</option>
                                            <option value="password">Password</option>
                                            <option value="tel">Mobile Number</option>
                                            <option value="url">URL</option>
                                            <option value="date">Date</option>
                                        </select>
                                    </div>
                                    <div className="rcp-field">
                                        <FieldLabel htmlFor="otherSize" text="Input Size" tooltip="Roughly how wide the specify text box renders - a bigger number gives respondents a wider box to type into." />
                                        <input
                                            type="number"
                                            id="otherSize"
                                            value={item?.otherSize ?? ''}
                                            onChange={(e) => handlePropertyChange(index, 'otherSize', e.target.value === '' ? '' : Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                                {item?.otherRequired === false && (
                                    <p className="rcp-hint" style={{ marginTop: 8 }}>
                                        Respondents can select "Other" and proceed without entering any text in the specify box.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Logic & Quotas ── */}
                    {!item?.header && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faFilter} /> Logic &amp; Quotas
                            </h4>
                            <div className="rcp-grid">
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="quotaCounts" text="Quota Counts" tooltip="How many respondents can select this option before it counts toward/closes a quota." />
                                    <input
                                        type="text"
                                        id="quotaCounts"
                                        value={item?.quotaCounts || ''}
                                        onChange={(e) => handlePropertyChange(index, 'quotaCounts', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="filterLogic" text="Filter Logic" tooltip="A logic expression controlling whether this option is even shown to a given respondent." />
                                    <input
                                        type="text"
                                        id="filterLogic"
                                        value={item?.filterLogic || ''}
                                        onChange={(e) => handlePropertyChange(index, 'filterLogic', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="termLogic" text="Termination Logic" tooltip="A logic expression that, if true once this option is picked, ends the survey for that respondent." />
                                    <input
                                        type="text"
                                        id="termLogic"
                                        value={item?.termLogic || ''}
                                        onChange={(e) => handlePropertyChange(index, 'termLogic', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="punchLogic" text="Punch Logic" tooltip="A logic expression that auto-selects/deselects this option based on earlier answers, without the respondent touching it." />
                                    <input
                                        type="text"
                                        id="punchLogic"
                                        value={item?.punchLogic || ''}
                                        onChange={(e) => handlePropertyChange(index, 'punchLogic', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── String Grid Settings ── */}
                    {!item?.header && typeMatches(qType, "StringGrid") && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faTable} /> String Grid Settings
                            </h4>
                            <p className="rcp-hint">
                                Leave a field blank to use this question's global OE Parameters value for this row.
                            </p>
                            <div className="rcp-grid">
                                <div className="rcp-field rcp-field-full">
                                    <FieldLabel htmlFor="rowInputType" text="Input Type" tooltip="Override what kind of value this row's box accepts, instead of the question's global Input Type." />
                                    <select
                                        id="rowInputType"
                                        value={item?.inputType || ''}
                                        onChange={(e) => handlePropertyChange(index, 'inputType', e.target.value)}
                                    >
                                        <option value="">Use Global Default</option>
                                        <option value="text">Text</option>
                                        <option value="email">Email</option>
                                        <option value="password">Password</option>
                                        <option value="tel">Mobile Number</option>
                                        <option value="url">URL</option>
                                        <option value="date">Date</option>
                                    </select>
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="rowPreText" text="Prefix" tooltip="Text shown right before this row's answer box, e.g. a currency symbol." />
                                    <input
                                        type="text"
                                        id="rowPreText"
                                        placeholder="Use global default"
                                        value={item?.preText || ''}
                                        onChange={(e) => handlePropertyChange(index, 'preText', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="rowPostText" text="Suffix" tooltip="Text shown right after this row's answer box, e.g. a unit." />
                                    <input
                                        type="text"
                                        id="rowPostText"
                                        placeholder="Use global default"
                                        value={item?.postText || ''}
                                        onChange={(e) => handlePropertyChange(index, 'postText', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Numeric Grid Settings ── */}
                    {!item?.header && typeMatches(qType, "NumericGrid") && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faTable} /> Numeric Grid Settings
                            </h4>
                            <p className="rcp-hint">
                                Leave a field blank to use this question's global OE Parameters value for this row.
                            </p>
                            {/* Prefix/Suffix are plain text (same as the String Grid
                                fields above) - Min/Max/Decimal Places are numeric-
                                only (type="number"), since unlike Prefix/Suffix
                                they're actual numeric constraints, not free text. */}
                            <div className="rcp-grid">
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="rowPreTextNum" text="Prefix" tooltip="Text shown right before this row's numeric input, e.g. a currency symbol." />
                                    <input
                                        type="text"
                                        id="rowPreTextNum"
                                        placeholder="Use global default"
                                        value={item?.preText || ''}
                                        onChange={(e) => handlePropertyChange(index, 'preText', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="rowPostTextNum" text="Suffix" tooltip="Text shown right after this row's numeric input, e.g. a unit like %." />
                                    <input
                                        type="text"
                                        id="rowPostTextNum"
                                        placeholder="Use global default"
                                        value={item?.postText || ''}
                                        onChange={(e) => handlePropertyChange(index, 'postText', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="rowMin" text="Minimum Value" tooltip="The lowest number this row's input will accept." />
                                    <input
                                        type="number"
                                        id="rowMin"
                                        placeholder="Use global default"
                                        value={item?.min ?? ''}
                                        onChange={(e) => handlePropertyChange(index, 'min', e.target.value === '' ? '' : Number(e.target.value))}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="rowMax" text="Maximum Value" tooltip="The highest number this row's input will accept." />
                                    <input
                                        type="number"
                                        id="rowMax"
                                        placeholder="Use global default"
                                        value={item?.max ?? ''}
                                        onChange={(e) => handlePropertyChange(index, 'max', e.target.value === '' ? '' : Number(e.target.value))}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="rowPrefill" text="Prefill" tooltip="A default value already filled into this row's box before the respondent types anything." />
                                    <input
                                        type="number"
                                        id="rowPrefill"
                                        placeholder="Use global default"
                                        value={item?.prefill ?? ''}
                                        onChange={(e) => handlePropertyChange(index, 'prefill', e.target.value === '' ? '' : Number(e.target.value))}
                                    />
                                </div>
                            </div>

                            <div className="rcp-checks" style={{ marginTop: '12px' }}>
                                <CheckPill
                                    id="rowIsDecimal"
                                    label="Allow Decimals"
                                    checked={item?.isDecimal}
                                    onChange={(e) => handlePropertyChange(index, 'isDecimal', e.target.checked)}
                                    tooltip="Lets this row's answer include a decimal point instead of whole numbers only."
                                />
                            </div>

                            {item?.isDecimal && (
                                <div className="rcp-grid" style={{ marginTop: '12px' }}>
                                    <div className="rcp-field">
                                        <FieldLabel htmlFor="rowDecimalPlaces" text="Number of Decimal Places" tooltip="How many digits are allowed after the decimal point for this row." />
                                        <input
                                            type="number"
                                            id="rowDecimalPlaces"
                                            min="0"
                                            placeholder="Use global default"
                                            value={item?.decimalPlaces ?? ''}
                                            onChange={(e) => handlePropertyChange(index, 'decimalPlaces', e.target.value === '' ? '' : Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Custom Rows extras ── */}
                    {typeMatches(qType, "CustomRows") && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faTable} /> Custom Row Settings
                            </h4>
                            <div className="rcp-grid">
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="Pretext" text="Pretext" tooltip="Text shown right before this row's custom input." />
                                    <input
                                        type="text"
                                        id="Pretext"
                                        value={item?.Pretext || ''}
                                        onChange={(e) => handlePropertyChange(index, 'Pretext', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="Posttext" text="Posttext" tooltip="Text shown right after this row's custom input." />
                                    <input
                                        type="text"
                                        id="Posttext"
                                        value={item?.Posttext || ''}
                                        onChange={(e) => handlePropertyChange(index, 'Posttext', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="min" text="Min" tooltip="Lowest allowed value for this row, when its Type is one of the Open Numeric options." />
                                    <input
                                        type="text"
                                        id="min"
                                        value={item?.min || ''}
                                        onChange={(e) => handlePropertyChange(index, 'min', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <FieldLabel htmlFor="max" text="Max" tooltip="Highest allowed value for this row, when its Type is one of the Open Numeric options." />
                                    <input
                                        type="text"
                                        id="max"
                                        value={item?.max || ''}
                                        onChange={(e) => handlePropertyChange(index, 'max', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field rcp-field-full">
                                    <FieldLabel htmlFor="type" text="Type" tooltip="How this row renders/behaves: a single choice, multiple choice, a pinned/frozen label, or an open numeric/text entry." />
                                    <select
                                        id="type"
                                        value={item?.type || ''}
                                        onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                                    >
                                        <option value="">Select type</option>
                                        <option value="radio">Radio</option>
                                        <option value="multi">Multi</option>
                                        <option value="freeze">Freeze</option>
                                        <option value="open_numeric_D">Open Numeric Decimal</option>
                                        <option value="open_numeric">Open Numeric</option>
                                        <option value="open_string">Open String</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Custom Cols extras ── */}
                    {typeMatches(qType, "CustomCols") && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faTable} /> Custom Column Settings
                            </h4>
                            <div className="rcp-grid">
                                <div className="rcp-field rcp-field-full">
                                    <FieldLabel htmlFor="colType" text="Column Type" tooltip="What kind of data/question this column represents in the custom grid." />
                                    <select
                                        id="colType"
                                        value={item?.type || ''}
                                        onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                                    >
                                        <option value="">Select type</option>
                                        <option value="String">String</option>
                                        <option value="Numeric">Numeric</option>
                                        <option value="SingleResponse">Single Response</option>
                                        <option value="MultiResponse">Multi Response</option>
                                        <option value="single2dgrid">Single 2D Grid</option>
                                        <option value="multigrid">Multi Grid</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - same layout/buttons as RedirectModal. Edits in this
                    popup apply to the row live as you type, so Save here just
                    confirms & closes; the survey itself is persisted by the
                    builder's Save Survey button, exactly as before. */}
                <div className="rcp-footer">
                    <button className="rcp-cancel-btn" onClick={() => onClose()}>
                        Close
                    </button>
                    <button className="rcp-save-btn" onClick={() => onClose()}>
                        <FontAwesomeIcon icon={faSave} /> Save
                    </button>
                </div>
            </div>

            {/* Image lightbox - above the popup; click anywhere (or the ✕) to close */}
            {imgZoomOpen && item?.imageURL && (
                <div className="rcp-zoom-overlay" onClick={(e) => { e.stopPropagation(); setImgZoomOpen(false); }}>
                    <button className="rcp-zoom-close-btn" title="Close zoom">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <img src={item.imageURL} alt="full size preview" />
                </div>
            )}
        </div>
    );
};

export default RowColumnProperties;
